/**
 * features/reports/VatReportPage.tsx — รายงานภาษีขาย/ภาษีซื้อรายเดือน
 */

import { useState } from 'react';
import { FileDown, Printer } from 'lucide-react';
import { formatDate, formatMoney, formatMonthYearThai } from '@ft/core';
import type { VatReportRow } from '~/lib/types';
import { resolveApiUrl } from '~/lib/api';
import { PageHeader } from '~/ui/layout';
import { Button, Card, CardHeader, EmptyState, Select, TableSkeleton } from '~/ui/primitives';
import { useVatReport } from './hooks';

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => index + 1);

function ReportTable({
  rows,
  emptyLabel,
  numberHeader,
  partyHeader,
}: {
  rows: VatReportRow[];
  emptyLabel: string;
  numberHeader: string;
  partyHeader: string;
}) {
  if (rows.length === 0) {
    return <EmptyState title={emptyLabel} />;
  }

  const totalBeforeVat = rows.reduce((sum, row) => sum + row.amountBeforeVat, 0);
  const totalVat = rows.reduce((sum, row) => sum + row.vatAmount, 0);
  const grandTotal = rows.reduce((sum, row) => sum + row.totalAmount, 0);

  return (
    <div className="overflow-x-auto">
      <table className="table w-full">
        <thead>
          <tr>
            <th className="w-12 text-center">#</th>
            <th className="w-24">วันที่</th>
            <th className="w-36">{numberHeader}</th>
            <th>{partyHeader}</th>
            <th className="w-36 text-center">เลขผู้เสียภาษี</th>
            <th className="w-16 text-center">สาขา</th>
            <th className="w-32 text-right">มูลค่าก่อนภาษี</th>
            <th className="w-28 text-right">VAT</th>
            <th className="w-32 text-right">รวม</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id}>
              <td className="tabular text-center text-fg-muted">{index + 1}</td>
              <td className="tabular text-fg-muted">{formatDate(row.date)}</td>
              <td className="font-medium text-fg">{row.documentNumber}</td>
              <td className="text-fg-muted">{row.partyName || '-'}</td>
              <td className="tabular text-center text-fg-muted text-xs">{row.partyTaxId || '-'}</td>
              <td className="tabular text-center text-fg-muted text-xs">
                {row.branch && row.branch !== '00000' && row.branch !== 'สำนักงานใหญ่'
                  ? row.branch
                  : row.partyTaxId
                    ? 'สนญ.'
                    : '-'}
              </td>
              <td className="tabular text-right text-fg-muted">{formatMoney(row.amountBeforeVat)}</td>
              <td className="tabular text-right text-fg-muted">{formatMoney(row.vatAmount)}</td>
              <td className="tabular text-right font-medium text-fg">{formatMoney(row.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border-base bg-surface-hover font-semibold">
            <td colSpan={6} className="px-3 py-2 text-fg">
              รวม ({rows.length} รายการ)
            </td>
            <td className="tabular px-3 py-2 text-right text-fg">{formatMoney(totalBeforeVat)}</td>
            <td className="tabular px-3 py-2 text-right text-accent-fg">{formatMoney(totalVat)}</td>
            <td className="tabular px-3 py-2 text-right font-bold text-fg">{formatMoney(grandTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function VatReportPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(CURRENT_MONTH);
  const { data, isLoading } = useVatReport(year, month);

  function handleOpenPdf(type: 'sales' | 'purchases') {
    window.open(resolveApiUrl(`/api/reports/vat/${type}/pdf?year=${year}&month=${month}`, true), '_blank');
  }

  function handlePrint() {
    window.print();
  }

  return (
    <div className="vat-report-page">
      <PageHeader
        title="รายงานภาษีมูลค่าเพิ่ม"
        description={formatMonthYearThai(year, month)}
        actions={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <Select value={month} onChange={(event) => setMonth(Number(event.target.value))} className="w-32">
              {MONTH_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {formatMonthYearThai(year, value).split(' ')[0]}
                </option>
              ))}
            </Select>
            <Select value={year} onChange={(event) => setYear(Number(event.target.value))} className="w-24">
              {YEAR_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value + 543}
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              onClick={() => handleOpenPdf('sales')}
              className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
            >
              <FileDown size={15} />
              PDF ภาษีขาย
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleOpenPdf('purchases')}
              className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950/30"
            >
              <FileDown size={15} />
              PDF ภาษีซื้อ
            </Button>
            <Button variant="ghost" onClick={handlePrint} className="gap-1.5" title="พิมพ์หน้านี้">
              <Printer size={15} />
              พิมพ์
            </Button>
          </div>
        }
      />

      {isLoading || !data ? (
        <Card>
          <TableSkeleton columns={9} />
        </Card>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 print:hidden">
            <div className="card p-4">
              <p className="text-xs text-fg-muted">ภาษีขาย</p>
              <p className="tabular mt-1 text-xl font-semibold text-emerald-600 dark:text-emerald-400">
                {formatMoney(data.totals.salesVat)}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-fg-muted">ภาษีซื้อ</p>
              <p className="tabular mt-1 text-xl font-semibold text-rose-600 dark:text-rose-400">
                {formatMoney(data.totals.purchasesVat)}
              </p>
            </div>
            <div className="card p-4">
              <p className="text-xs text-fg-muted">
                {data.totals.vatPayable >= 0 ? 'ภาษีที่ต้องนำส่ง' : 'ภาษีที่ขอคืนได้'}
              </p>
              <p
                className={`tabular mt-1 text-xl font-semibold ${
                  data.totals.vatPayable >= 0 ? 'text-danger' : 'text-success'
                }`}
              >
                {formatMoney(Math.abs(data.totals.vatPayable))}
              </p>
            </div>
          </div>

          <Card className="print:shadow-none print:border-none">
            <CardHeader
              title="ภาษีขาย"
              description="จากใบเสร็จ/ใบกำกับภาษีที่ออกให้ลูกค้า"
              actions={
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenPdf('sales')}
                  className="gap-1 text-xs print:hidden"
                >
                  <FileDown size={14} />
                  พิมพ์ PDF ภาษีขาย
                </Button>
              }
            />
            <ReportTable
              rows={data.sales}
              emptyLabel="ไม่มีรายการภาษีขายในเดือนนี้"
              numberHeader="เลขที่ใบกำกับภาษี"
              partyHeader="ลูกค้า"
            />
          </Card>

          <Card className="print:shadow-none print:border-none print:break-before-page">
            <CardHeader
              title="ภาษีซื้อ"
              description="จากค่าใช้จ่ายที่มีใบกำกับภาษี"
              actions={
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleOpenPdf('purchases')}
                  className="gap-1 text-xs print:hidden"
                >
                  <FileDown size={14} />
                  พิมพ์ PDF ภาษีซื้อ
                </Button>
              }
            />
            <ReportTable
              rows={data.purchases}
              emptyLabel="ไม่มีรายการภาษีซื้อในเดือนนี้"
              numberHeader="รายการ/เลขที่เอกสาร"
              partyHeader="ผู้ขาย/คู่ค้า"
            />
          </Card>
        </div>
      )}
    </div>
  );
}
